import { fullCurrencyFormatter } from '@shared/helpers/format-currency';
import type { RowData } from '@tanstack/react-table';
import { defineColumn, type StrictColumnDef } from '../../../components/tanstack-table/column';
import type { ProductOrder } from '../../../data/receiving';

export function productLabel(p: ProductOrder): string {
  return p.brand ? `${p.brand} | ${p.description}` : p.description;
}

export function emDashIfEmpty(s: string): string {
  return s ? s : '—';
}

export function productHeadColumns<T extends RowData>(
  product: (row: T) => ProductOrder
): StrictColumnDef<T>[] {
  return [
    defineColumn<T>({
      id: 'Product',
      minWidth: 280,
      maxWidth: 520,
      accessorFn: (r) => productLabel(product(r)),
    }),
    defineColumn<T>({
      id: 'Size',
      minWidth: 80,
      maxWidth: 120,
      accessorFn: (r) => product(r).size,
    }),
  ];
}

/**
 * Vendor provenance column, placed directly after Size. Only rendered when a
 * session holds more than one vendor — single-vendor sessions stay identical to
 * what stores see today.
 *
 * Not rightmost by design: the rightmost virtualized column collides with the
 * overlay scrollbar.
 */
export function vendorColumn<T extends RowData>(
  product: (row: T) => ProductOrder,
  vendorName: (vendorId: string) => string
): StrictColumnDef<T> {
  return defineColumn<T>({
    id: 'Vendor',
    minWidth: 120,
    maxWidth: 220,
    accessorFn: (r) => emDashIfEmpty(vendorName(product(r).vendor_id)),
  });
}

export function productTailColumns<T extends RowData>(
  product: (row: T) => ProductOrder
): StrictColumnDef<T>[] {
  return [
    defineColumn<T>({
      id: 'Qty/Case',
      minWidth: 70,
      maxWidth: 90,
      accessorFn: (r) => String(product(r).case_qty),
      cell: ({ value }) => <div className="w-full text-right">{value}</div>,
      sortingFn: (a, b) => product(a).case_qty - product(b).case_qty,
    }),
    defineColumn<T>({
      id: 'Cases',
      minWidth: 60,
      maxWidth: 80,
      accessorFn: (r) => String(product(r).cases_ordered),
      cell: ({ value }) => <div className="w-full text-right">{value}</div>,
      sortingFn: (a, b) => product(a).cases_ordered - product(b).cases_ordered,
    }),
    defineColumn<T>({
      id: 'Units',
      minWidth: 60,
      maxWidth: 80,
      accessorFn: (r) => String(product(r).units_ordered),
      cell: ({ value }) => <div className="w-full text-right">{value}</div>,
      sortingFn: (a, b) => product(a).units_ordered - product(b).units_ordered,
    }),
    defineColumn<T>({
      id: 'Extended',
      minWidth: 90,
      maxWidth: 120,
      accessorFn: (r) => fullCurrencyFormatter(product(r).sales_price),
      cell: ({ value }) => <div className="w-full text-right">{value}</div>,
      sortingFn: (a, b) => product(a).sales_price - product(b).sales_price,
    }),
  ];
}
